import { useEffect, useState, MouseEvent as ReactMouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Footer from '../../components/layout/Footer';
import StudentSearch from '../../components/student/student/Search';
import StudentTable from '../../components/student/student/Table';
import StudentInfoEditor from '../../components/student/student/InfoEditor';
import ParentInfoEditor from '../../components/student/student/ParentInfoEditor';
import StudentMemo from '../../components/student/student/Memo';
import StudentCounsel from '../../components/student/student/Counsel';
import StudentClass from '../../components/student/student/class/Class';
import StudentMemoTab from '../../components/student/student/MemoTab';
import StudentCoupon from '../../components/student/student/Coupon';
import StudentTab from '../../components/student/student/Tab';
import useAxios from '../../hooks/useAxios';
import {
  getParent,
  getStudent,
  getStudentClassInfo,
} from '../../api/students.api';
import {
  IGetParent,
  IGetStudent,
  IGetStudentClassInfo,
} from '../../models/students.model';
import StudentAttendance from '../../components/student/student/Attendance';
import {
  setPaymentMode,
  setPaymentStep,
} from '../../store/student.student.slice';
import ExtendButton from '../../components/ExtendButton';
import ShuttleInfo from '../../components/student/student/ShuttleInfo';
import StudentRefund from '../../components/student/student/Refund';
import StudentTest from '../../components/student/student/test/StudentTest';
import StudentMessage from '../../components/student/student/message/StudentMessage';
import ClassRefund from '../../components/class/class/Refund';
import ClassInfo from '../../components/student/student/ClassInfo';
import { useTranslation } from 'react-i18next';
import ScoreContainer from '../../components/student/student/ScoreContainer';
import PointContainer from '../../components/student/student/PointContainer';
import BookTab from '../../components/student/student/book/BookTab';
import StudentPaymentPane from '../../components/student/student/StudentPaymentPane';
import StudentSchoolRecordPane from '../../components/student/student/StudentSchoolRecordPane';
import { qsParse } from '../../lib/util';
import StudentArticlePane from '../../components/student/student/StudentArticlePane';
import useLoader from '../../hooks/useLoader';
import AwardsList from '../../components/student/student/AwardsList';
import { RootState } from '../../store/rootReducer';
import { setStudentTabIndex } from '../../store/class.slice';
import Split from 'react-split';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';

const Block = styled.div`
  .close-left {
    transform: translate(-41.5%);
  }
    
  .table-inside {
    width: 100%;

    th {
      background: ghostwhite;
    }
  }

  .img-preview {
    max-width: 150px;
  }
`;

export enum StudentContents {
  Info,
  Class,
  Counsel,
  Payment,
  Refund,
  Coupon,
  Score,
  Point,
  Attendance,
  AttendanceRefund,
  Test,
  Book,
  Article,
  SchoolRecord,
  Memo,
  Message,
  Award,
}

const StudentStudent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isDirectCounsel } = useSelector((state: RootState) => state?.class);
  const query = qsParse(location.search);
  const { data: res, isLoading, error, execute } = useAxios<IGetStudent>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetParent>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetStudentClassInfo>();
  //useLoader(isLoading || isLoading2 || isLoading3, [error, error2, error3]);

  const [isExtended, setIsExtended] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isStudentEdit, setIsStudentEdit] = useState(false);
  const [isParentEdit, setIsParentEdit] = useState(false);
  const [memberCode, setMemberCode] = useState('');
  const [campusCode, setCampusCode] = useState('');

  useEffect(() => {
    if (memberCode && campusCode) {
      setIsStudentEdit(false);
      setIsParentEdit(false);
      execute(getStudent(memberCode, campusCode));
      execute2(getParent(memberCode));
      doGetStudentClassInfo();
    }
  }, [memberCode, campusCode]);

  useEffect(() => {
    if (query.memberCode && query.campusCode) {
      setMemberCode(query.memberCode as string);
      setCampusCode(query.campusCode as string);
    }
    if (query.tabIndex) {
      // tabIndex: 외부 메뉴에서 탭 호출
      const _tabIndex = Number(query.tabIndex);
      if (_tabIndex === StudentContents.Payment) {
        dispatch(setPaymentMode('acceptance'));
        dispatch(setPaymentStep('before'));
      }
      setTabIndex(_tabIndex);
    }
  }, [location]);

  const doGetStudentClassInfo = () => {
    if (memberCode) {
      execute3(getStudentClassInfo(memberCode));
    }
  };

  //20240507 aiden 학생대시보드 > 학생 클릭 후  학생관리 넘어왔을때 상담 탭 이동을 위한 tab번호 하드코딩
  useEffect(() => {
    if (isDirectCounsel) {
      setTabIndex(2);
      dispatch(setStudentTabIndex(false));
    }
  }, [isDirectCounsel]);

  const renderTabContent = () => {
    switch (tabIndex) {
      case StudentContents.Info:
        return (
          <>
            <StudentInfoEditor
              student={res?.data}
              isEdit={isStudentEdit}
              onEditClick={onStudentEditClick}
              studentExecute={execute}
              setMemberCode={setMemberCode}
              setCampusCode={setCampusCode}
            />
            <ClassInfo classInfo={res3?.data} />
            <ShuttleInfo student={res?.data} />
            <ParentInfoEditor
              student={res?.data}
              parent={res2?.data}
              isEdit={isParentEdit}
              onEditClick={onParentEditClick}
            />
            <StudentMemo student={res?.data} onTabChange={onTabChange} />
          </>
        );
      case StudentContents.Class:
        return (
          <StudentClass
            student={res?.data}
            onTabChange={onTabChange}
            doGetStudentClassInfo={doGetStudentClassInfo}
          />
        );
      case StudentContents.Counsel:
        return <StudentCounsel student={res?.data} classInfo={res3?.data} />;
      case StudentContents.Payment:
        return <StudentPaymentPane student={res?.data} parent={res2?.data} setTabIndex={setTabIndex} />;
      case StudentContents.Refund:
        return <StudentRefund student={res?.data} />;
      case StudentContents.Coupon:
        return <StudentCoupon student={res?.data} />;
      case StudentContents.Score:
        return <ScoreContainer student={res?.data} />;
      case StudentContents.Point:
        return <PointContainer student={res?.data} />;
      case StudentContents.Attendance:
        return (
          <StudentAttendance student={res?.data} setTabIndex={setTabIndex} />
        );
      // 출결 -> 인정결석 환불
      case StudentContents.AttendanceRefund:
        return (
          <ClassRefund
            setTab={(tab: string) => setTabIndex(Number(tab))}
            tabOrigin={String(StudentContents.Attendance)}
          />
        );
      case StudentContents.Test:
        return <StudentTest student={res?.data} />;
      // 도서 탭
      case StudentContents.Book:
        return <BookTab student={res?.data} />;
      case StudentContents.Article:
        return (
          <StudentArticlePane student={res?.data} classInfo={res3?.data} />
        );
      case StudentContents.SchoolRecord:
        return <StudentSchoolRecordPane student={res?.data} />;
      case StudentContents.Memo:
        return <StudentMemoTab student={res?.data} />;
      case StudentContents.Message:
        return <StudentMessage student={res?.data} parent={res2?.data} />;
      case StudentContents.Award:
        return <AwardsList student={res?.data} classInfo={res3?.data} />;
    }
  };

  const onTabClick = (e: ReactMouseEvent, tabIndex: number) => {
    e.preventDefault();
    onTabChange(tabIndex);
  };

  const onTabChange = (tabIndex: number) => {
    setTabIndex(tabIndex);

    if (tabIndex === 3) {
      dispatch(setPaymentMode('breakdown'));
      dispatch(setPaymentStep('before'));
    }
  };

  const onStudentEditClick = () => setIsStudentEdit(!isStudentEdit);

  const onParentEditClick = () => setIsParentEdit(!isParentEdit);

  return (
    <>
    <StudentSearch />
    <Block className="content">
      <SimpleHeader
        iconFile="icon-student-big.svg"
        parentMenu={t('common.menu.student')}
        currentMenu={t('student.student.title1')}
        onlyTitle={true}
      />
      
      { (isLoading || isLoading2 || isLoading3) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <div>
        <Split 
            className='splite-divide'
            sizes={[37, 63]}
            minSize={100}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
          >
            <div className='col-frame left_panel'>
              <StudentTable
                memberCode={memberCode}
                setMemberCode={setMemberCode}
                setCampusCode={setCampusCode}
              />
            </div>
            <div className='col-frame right_panel width-type'>
              <StudentTab
                tabIndex={tabIndex}
                student={memberCode ? res?.data : undefined}
                renderTabContent={renderTabContent}
                onClick={onTabClick}
              />
            </div>
          </Split>
      </div>
      <Footer />
    </Block>
    </>
  );
};

export default StudentStudent;
